import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faUser, faCheck, faTimes, faGraduationCap, faStar, faMapMarkerAlt, faBriefcase, faMapMarkedAlt, faSpinner, faFilter } from '@fortawesome/free-solid-svg-icons';
import '../styles/CompanyMap.css';
import { useToast } from '../components/Admissionform/ToastManager';


const CompanyMap = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState(null);
  const [matchingStudents, setMatchingStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [isMapping, setIsMapping] = useState(false);
  const [mappingStatusFilter, setMappingStatusFilter] = useState('all'); // 'all', 'mapped', 'not-mapped'
  const [counts, setCounts] = useState({ all: 0, mapped: 0, notMapped: 0 });
  const { showSuccess, showError } = useToast();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalStudents: 0,
    studentsPerPage: 10
  });

  // Get placement ID from location state
  const placementId = location.state?.recordId;
  const companyName = location.state?.companyName;
  const companyLocation = location.state?.companyLocation;

  useEffect(() => {
    if (placementId) {
      fetchMatchingStudents(1, mappingStatusFilter);
    } else {
      setError('No placement ID provided');
      setIsLoading(false);
    }
  }, [placementId, mappingStatusFilter]);

  const fetchMatchingStudents = async (page = 1, filter = 'all', limit = 10) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found. Please login again.');
        setIsLoading(false);
        return;
      }

      // Add mappingFilter parameter to the API call
      const response = await fetch(`${process.env.REACT_APP_API_URL}/placements/${placementId}/matching-students?page=${page}&limit=${limit}&mappingFilter=${filter}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Matching students data:', result);

        if (result.success) {
          setCompanyData(result.data.placement);
          setMatchingStudents(result.data.matchingStudents);
          setPagination(result.data.pagination);
          
          // Set counts from API response
          if (result.data.counts) {
            setCounts(result.data.counts);
          }
        } else {
          setError(result.message || 'Failed to fetch matching students');
        }
      } else {
        const errorData = await response.json();
        if (response.status === 401) {
          setError('Authentication failed. Please login again.');
        } else {
          setError(errorData.message || 'Failed to fetch matching students');
        }
      }
    } catch (error) {
      console.error('Error fetching matching students:', error);
      if (error.name === 'TypeError') {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle mapping status filter change
  const handleMappingStatusFilterChange = (filterValue) => {
    setMappingStatusFilter(filterValue);
    setSelectedStudents(new Set()); // Clear selections when filter changes
    // fetchMatchingStudents will be called by useEffect
  };

  // Get filtered count for display - now from API response
  const getFilteredCount = () => {
    return pagination.totalStudents; // This is already the filtered count from backend
  };

  // Handle individual checkbox selection
  const handleStudentSelection = (studentId, isSelected) => {
    const newSelectedStudents = new Set(selectedStudents);
    if (isSelected) {
      newSelectedStudents.add(studentId);
    } else {
      newSelectedStudents.delete(studentId);
    }
    setSelectedStudents(newSelectedStudents);
  };

  // Handle select all checkbox - works with current page students
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const currentPageStudentIds = matchingStudents.map(student => student.studentId);
      const newSelectedStudents = new Set([...selectedStudents, ...currentPageStudentIds]);
      setSelectedStudents(newSelectedStudents);
    } else {
      const currentPageStudentIds = new Set(matchingStudents.map(student => student.studentId));
      const newSelectedStudents = new Set([...selectedStudents].filter(id => !currentPageStudentIds.has(id)));
      setSelectedStudents(newSelectedStudents);
    }
  };

  // Check functions - use current page students
  const isAllCurrentPageSelected = () => {
    return matchingStudents.length > 0 && matchingStudents.every(student => selectedStudents.has(student.studentId));
  };

  const isSomeCurrentPageSelected = () => {
    return matchingStudents.some(student => selectedStudents.has(student.studentId));
  };

  // Handle mapping selected students - updated to set isAccepted as null
  const handleMapStudents = async () => {
    if (selectedStudents.size === 0) {
      showError("Please select at least one student to map.");
      return;
    }

    setIsMapping(true);
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');

    try {
      const mappingPromises = Array.from(selectedStudents).map(studentId => {
        const student = matchingStudents.find(s => s.studentId === studentId);
        const databaseId = student ? student.id : null;

        if (!databaseId) {
          console.error(`Database ID not found for studentId: ${studentId}`);
          return Promise.reject(new Error(`Database ID not found for student: ${studentId}`));
        }

        return fetch(`${process.env.REACT_APP_API_URL}/map`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            companyId: placementId,
            companyName: companyData?.companyName || companyName || 'Unknown Company',
            studentId: student.studentId,
            interviewDate: companyData?.dateOfPlacement || null,
            isAccepted: null,
            isSelected: false
          })
        }).then(async response => {
          const responseData = await response.json();

          if (!response.ok) {
            if (response.status === 409) {
              throw new Error(responseData.message || `Student ${studentId} is already mapped to this company`);
            } else if (response.status === 400) {
              throw new Error(responseData.message || `Invalid data for student ${studentId}`);
            } else {
              throw new Error(responseData.message || `Failed to map student ${studentId}`);
            }
          }

          return { studentId, data: responseData };
        });
      });

      const results = await Promise.allSettled(mappingPromises);

      const successful = [];
      const failed = [];
      const alreadyMapped = [];

      results.forEach((result, index) => {
        const studentId = Array.from(selectedStudents)[index];

        if (result.status === 'fulfilled') {
          successful.push(studentId);
        } else if (result.status === 'rejected') {
          const errorMessage = result.reason.message;

          if (errorMessage.includes('already mapped')) {
            alreadyMapped.push(studentId);
          } else {
            failed.push({ studentId, error: errorMessage });
          }
        }
      });

      // Use toast messages instead of alert
      if (successful.length > 0) {
        showSuccess(`✓ Successfully mapped ${successful.length} student(s) to ${companyData?.companyName || companyName}! Students will receive notifications to accept/reject.`);
      }

      if (alreadyMapped.length > 0) {
        showError(`⚠ ${alreadyMapped.length} student(s) were already mapped to this company.`);
      }

      if (failed.length > 0) {
        showError(`✗ Failed to map ${failed.length} student(s). Please try again.`);
        failed.forEach(({ studentId, error }) => {
          console.error(`Failed to map student ${studentId}:`, error);
        });
      }

      if (successful.length > 0) {
        setSelectedStudents(new Set());
        fetchMatchingStudents(pagination.currentPage, mappingStatusFilter);
      }

    } catch (error) {
      console.error('Error mapping students:', error);
      showError('An unexpected error occurred while mapping students. Please try again.');
    } finally {
      setIsMapping(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    fetchMatchingStudents(pageNumber, mappingStatusFilter, pagination.studentsPerPage);
    // Scroll to top of students section
    document.querySelector('.students-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePrevious = () => {
    if (pagination.hasPreviousPage) {
      handlePageChange(pagination.currentPage - 1);
    }
  };

  const handleNext = () => {
    if (pagination.hasNextPage) {
      handlePageChange(pagination.currentPage + 1);
    }
  };

  // Function to get match score color
  const getMatchScoreColor = (score) => {
    if (score >= 80) return 'high-match';
    if (score >= 60) return 'medium-match';
    return 'low-match';
  };

  // Function to format skills display
  const formatSkills = (skillsString) => {
    if (!skillsString) return [];
    return skillsString.split(',').map(skill => skill.trim()).filter(skill => skill && skill.toLowerCase() !== 'null');
  };

  // Generate pagination numbers
  const generatePaginationNumbers = () => {
    const pages = [];
    const totalPages = pagination.totalPages;
    const currentPage = pagination.currentPage;

    // Always show first page
    pages.push(1);

    // Calculate start and end pages around current page
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    // Add ellipsis after first page if needed
    if (startPage > 2) {
      pages.push('...');
    }

    // Add pages around current page
    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(i);
      }
    }

    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      pages.push('...');
    }

    // Always show last page if it's different from first
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  if (isLoading) {
    return (
      <div className="company-map-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <h3>Loading Company Details...</h3>
          <p>Finding matching students...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-map-container">
        <div className="error-state">
          <h3>Error</h3>
          <p>{error}</p>
          <div className="error-buttons">
            <button className="retrycm-button" onClick={() => fetchMatchingStudents(1, mappingStatusFilter)}>
              Try Again
            </button>
            <button className="backcm-button" onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-map-container">
      {/* Header */}
      <div className="company-header">
        <h1 className="page-title">Company Overview & Matching Students</h1>
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
      </div>

      {/* Company Details Section */}
      <div className="company-details-section">
        <div className="company-card">
          <div className="company-card-header">
            <h2 className="company-name">{companyData?.companyName || companyName || 'N/A'}</h2>
            {companyLocation && (
              <div className="company-location">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>{companyLocation}</span>
              </div>
            )}
          </div>

          <div className="company-details-grid">
            <div className="detail-item">
              <FontAwesomeIcon icon={faBriefcase} className="detail-icon" />
              <div className="detail-content">
                <label>Job Role</label>
                <span>{companyData?.recruitmentRole || 'Not specified'}</span>
              </div>
            </div>

            <div className="detail-item">
              <FontAwesomeIcon icon={faGraduationCap} className="detail-icon" />
              <div className="detail-content">
                <label>Technology</label>
                <span>{companyData?.technology || 'Not specified'}</span>
              </div>
            </div>

            <div className="detail-item">
              <FontAwesomeIcon icon={faUser} className="detail-icon" />
              <div className="detail-content">
                <label>Department</label>
                <span>{companyData?.Department || 'Not specified'}</span>
              </div>
            </div>

            {/* Display Interview Date if available */}
            {companyData?.interviewDate && (
              <div className="detail-item">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="detail-icon" />
                <div className="detail-content">
                  <label>Interview Date</label>
                  <span>{new Date(companyData.interviewDate).toLocaleDateString()}</span>
                </div>
              </div>
            )}
          </div>

          {companyData?.requiredSkills && companyData.requiredSkills.length > 0 && (
            <div className="required-skills">
              <h3>Required Skills</h3>
              <div className="skills-list">
                {companyData.requiredSkills.map((skill, index) => (
                  <span key={index} className="skill-tag required-skill">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Students Section */}
      <div className="students-section">
        <div className="students-header">
          <div className="students-title-section">
            <h2>Matching Students ({getFilteredCount()})</h2>

            {/* Mapping Status Filter - now shows counts from API */}
            <div className="mapping-status-filter">
              <FontAwesomeIcon icon={faFilter} className="filter-icon" />
              <select
                value={mappingStatusFilter}
                onChange={(e) => handleMappingStatusFilterChange(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Students ({counts.all})</option>
                <option value="mapped">Mapped ({counts.mapped})</option>
                <option value="not-mapped">Not Mapped ({counts.notMapped})</option>
              </select>
            </div>
          </div>

          <div className="header-actions">
            {selectedStudents.size > 0 && (
              <div className="selection-info">
                <span>{selectedStudents.size} student(s) selected</span>
                <button
                  className="clear-selection-btn"
                  onClick={() => setSelectedStudents(new Set())}
                >
                  Clear Selection
                </button>
              </div>
            )}
            <div className="match-legend">
              <span className="legend-item">
                <span className="legend-dot high-match"></span>
                80%+ Match
              </span>
              <span className="legend-item">
                <span className="legend-dot medium-match"></span>
                60-79% Match
              </span>
              <span className="legend-item">
                <span className="legend-dot low-match"></span>
                Below 60% Match
              </span>
            </div>
          </div>
        </div>

        {matchingStudents.length === 0 ? (
          <div className="no-students">
            <FontAwesomeIcon icon={faUser} className="no-students-icon" />
            <h3>
              {mappingStatusFilter === 'all'
                ? 'No Matching Students Found'
                : mappingStatusFilter === 'mapped'
                  ? 'No Mapped Students Found'
                  : 'No Unmapped Students Found'
              }
            </h3>
            <p>
              {mappingStatusFilter === 'all'
                ? 'No students found with the required skills for this placement.'
                : mappingStatusFilter === 'mapped'
                  ? 'No students have been mapped to this company yet.'
                  : 'All matching students have already been mapped to this company.'
              }
            </p>
          </div>
        ) : (
          <>
            {/* Students Table */}
            <div className="students-table-container">
              <table className="students-table">
                <thead>
                  <tr>
                    <th className="select-column">
                      <input
                        type="checkbox"
                        checked={isAllCurrentPageSelected()}
                        ref={input => {
                          if (input) input.indeterminate = isSomeCurrentPageSelected() && !isAllCurrentPageSelected();
                        }}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        title="Select all students on this page"
                      />
                    </th>
                    <th>S.No</th>
                    <th>Student Details</th>
                    <th>Course</th>
                    <th>Phone</th>
                    <th>Match Score</th>
                    <th>Mapping Status</th>
                    <th className="skills-column">Skills</th>
                    <th className="skills-column">Matched Skills</th>
                  </tr>
                </thead>
                <tbody>
                  {matchingStudents.map((student, index) => {
                    const studentSkills = formatSkills(student.knownSkill);
                    const matchedSkills = student.matchedSkills || [];

                    return (
                      <tr key={student.id || index} className={selectedStudents.has(student.studentId) ? 'selected-row' : ''}>
                        <td className="select-column">
                          <input
                            type="checkbox"
                            checked={selectedStudents.has(student.studentId)}
                            onChange={(e) => handleStudentSelection(student.studentId, e.target.checked)}
                          />
                        </td>
                        <td>{((pagination.currentPage - 1) * pagination.studentsPerPage) + index + 1}</td>
                        <td>
                          <div className="student-name">{student.studentName || 'N/A'}</div>
                          <div className="student-email">{student.branch || 'N/A'}</div>
                          <div className="student-id">ID: {student.studentId || 'N/A'}</div>
                        </td>
                        <td>{student.courseName || 'N/A'}</td>
                        <td>{student.studentContactNumber || 'N/A'}</td>
                        <td>
                          <div className={`match-score ${getMatchScoreColor(student.matchScore || 0)}`}>
                            <FontAwesomeIcon icon={faStar} />
                            <span>{student.matchScore || 0}%</span>
                          </div>
                        </td>
                        <td className="mapping-status-column">
                          <div className={`mapping-status ${student.isMapped ? 'mapped' : 'not-mapped'}`}>
                            <FontAwesomeIcon icon={student.isMapped ? faCheck : faTimes} />
                            <span>{student.isMapped ? 'Mapped' : 'Not Mapped'}</span>
                          </div>
                        </td>
                        <td className="skills-column">
                          <div className="skills-cell">
                            {studentSkills.length > 0 ?
                              studentSkills.map((skill, skillIndex) => (
                                <span
                                  key={skillIndex}
                                  className={`skill-tag-small ${matchedSkills.some(ms => ms.toLowerCase().includes(skill.toLowerCase()) || skill.toLowerCase().includes(ms.toLowerCase())) ? 'matched-skill-small' : 'other-skill-small'}`}
                                >
                                  {skill}
                                </span>
                              )) :
                              <span className="no-skills-cell">No skills listed</span>
                            }
                          </div>
                        </td>
                        <td className="skills-column">
                          <div className="skills-cell">
                            {matchedSkills.length > 0 ?
                              matchedSkills.map((skill, skillIndex) => (
                                <span key={skillIndex} className="skill-tag-small matched-skill-small">
                                  {skill}
                                </span>
                              )) :
                              <span className="no-skills-cell">No matches</span>
                            }
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Map Selected Students Button */}
            {selectedStudents.size > 0 && (
              <div className="map-students-section">
                <button
                  className="map-students-btn"
                  onClick={handleMapStudents}
                  disabled={isMapping}
                >
                  {isMapping ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                      Mapping Students...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faMapMarkedAlt} />
                      Map Selected Students ({selectedStudents.size})
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination-wrapper">
                <div className="pagination-info">
                  Showing {((pagination.currentPage - 1) * pagination.studentsPerPage) + 1}-{Math.min(pagination.currentPage * pagination.studentsPerPage, pagination.totalStudents)} of {pagination.totalStudents} students
                </div>
                <div className="pagination-container">
                  <button
                    onClick={handlePrevious}
                    disabled={!pagination.hasPreviousPage}
                    className="pagination-btn"
                  >
                    &laquo;
                  </button>

                  {generatePaginationNumbers().map((pageNum, index) => {
                    if (pageNum === '...') {
                      return <span key={index} className="pagination-ellipsis">...</span>;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`pagination-btn ${pagination.currentPage === pageNum ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={handleNext}
                    disabled={!pagination.hasNextPage}
                    className="pagination-btn"
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CompanyMap;